package com.dotdrives.controller

import com.dotdrives.core.DotController
import com.dotdrives.model.Donation
import com.dotdrives.model.Target
import com.dotdrives.repository.DonationRepository
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlin.time.Clock
import kotlin.time.Instant

/**
 * One calendar month at a time (the design's "Aug 1 – Aug 31" range), navigable with
 * [DonationsLedgerController.goToPreviousMonth]/[DonationsLedgerController.goToNextMonth]/
 * [DonationsLedgerController.goToCurrentMonth] — `monthStart` is always the first instant of
 * the viewed month.
 */
data class DonationsLedgerUiState(
    val loading: Boolean = false,
    val donations: List<Donation> = emptyList(),
    val targets: List<Target> = emptyList(),
    val query: String = "",
    val targetFilter: String? = null,
    val sourceFilter: String? = null,
    val monthStart: Instant = Instant.fromEpochSeconds(0),
    val error: String? = null,
) {
    /** Nets reversals (negative `amount`) rather than summing absolute values. */
    val netTotal: Double get() = donations.filter(Donation::isActive).sumOf(Donation::amount)

    /** A reversal corrects a prior gift, it is not itself a second gift. */
    val giftCount: Int get() = donations.count { it.isActive && it.amount > 0 }

    val hasActiveFilters: Boolean get() = query.isNotBlank() || targetFilter != null || sourceFilter != null

    val syncErrorCount: Int get() = donations.count { it.qboSyncError != null }
}

class DonationsLedgerController(
    private val repo: DonationRepository,
    scope: CoroutineScope,
    private val orgId: String,
    private val clock: () -> Instant = { Clock.System.now() },
) : DotController<DonationsLedgerUiState>(
    DonationsLedgerUiState(monthStart = startOfMonth(clock())),
    scope,
) {
    private var searchJob: Job? = null
    private var generation = 0L

    fun load() {
        val gen = nextGeneration()
        update { it.copy(loading = true, error = null) }
        scope.launch {
            try {
                val targetsResult = repo.targets(orgId)
                val snapshot = state.value
                val listResult = repo.list(
                    orgId,
                    snapshot.monthStart,
                    startOfMonth(snapshot.monthStart, monthsAhead = 1),
                    snapshot.query.trim().takeIf(String::isNotEmpty),
                    snapshot.targetFilter,
                    snapshot.sourceFilter,
                )
                if (!isCurrent(gen)) return@launch
                listResult
                    .onSuccess { donations ->
                        update {
                            if (isCurrent(gen)) {
                                it.copy(
                                    loading = false,
                                    donations = donations,
                                    targets = targetsResult.getOrElse { emptyList() },
                                    error = null,
                                )
                            } else {
                                it
                            }
                        }
                    }
                    .onFailure { publishError(it, gen) }
            } catch (error: Exception) {
                publishError(error, gen)
            }
        }
    }

    fun search(query: String) {
        searchJob?.cancel()
        update { it.copy(query = query, error = null) }
        searchJob = scope.launch {
            delay(SEARCH_DEBOUNCE_MILLIS)
            load()
        }
    }

    fun setTargetFilter(targetId: String?) {
        searchJob?.cancel()
        update { it.copy(targetFilter = targetId, error = null) }
        load()
    }

    fun setSourceFilter(source: String?) {
        searchJob?.cancel()
        update { it.copy(sourceFilter = source, error = null) }
        load()
    }

    fun retry() = load()

    fun goToPreviousMonth() = setMonth(startOfMonth(state.value.monthStart, monthsAhead = -1))

    fun goToNextMonth() = setMonth(startOfMonth(state.value.monthStart, monthsAhead = 1))

    fun goToCurrentMonth() = setMonth(startOfMonth(clock()))

    private fun setMonth(newStart: Instant) {
        if (newStart == state.value.monthStart) return
        searchJob?.cancel()
        update { it.copy(monthStart = newStart, error = null) }
        load()
    }

    private fun publishError(error: Throwable, generation: Long) {
        if (error is CancellationException) throw error
        if (!isCurrent(generation)) return
        update { if (isCurrent(generation)) it.copy(loading = false, error = error.message ?: "Something went wrong") else it }
    }

    private fun nextGeneration(): Long {
        generation += 1
        return generation
    }

    private fun isCurrent(generation: Long): Boolean = generation == this.generation
}

/** UTC calendar-month boundary for the current instant (or `monthsAhead` months later). No
 * timezone/date library dependency needed: the instant's own ISO string carries the UTC
 * calendar date directly. */
internal fun startOfMonth(now: Instant, monthsAhead: Int = 0): Instant {
    val iso = now.toString()
    val year = iso.substring(0, 4).toInt()
    val month = iso.substring(5, 7).toInt()
    var totalMonths = (year * 12) + (month - 1) + monthsAhead
    val newYear = totalMonths / 12
    val newMonth = (totalMonths % 12) + 1
    val monthText = if (newMonth < 10) "0$newMonth" else "$newMonth"
    return Instant.parse("$newYear-$monthText-01T00:00:00Z")
}

private const val SEARCH_DEBOUNCE_MILLIS = 250L
